/**
 * Cloudflare Turnstile widget.
 *
 * Loaded at runtime from Cloudflare, never bundled: the site key comes from
 * GET /auth/providers so rotating it does not require a frontend rebuild, and
 * the secret never reaches this file. `action` is sent with the widget and
 * checked again by siteverify, so a login token cannot be replayed as an invite.
 */
import { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      theme?: 'light' | 'dark' | 'auto';
      action?: string;
      callback?: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    },
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadApi(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-vgn-turnstile]');
    const onReady = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile script loaded without an API.'));
    };
    if (existing) {
      existing.addEventListener('load', onReady, { once: true });
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed to load.')), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.dataset.vgnTurnstile = '1';
    script.addEventListener('load', onReady, { once: true });
    script.addEventListener('error', () => reject(new Error('Turnstile script failed to load.')), { once: true });
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export interface TurnstileWidgetProps {
  siteKey: string;
  /** Bound into the token and checked again by the server. */
  action: 'login' | 'invite' | 'destroy' | 'provision';
  theme?: 'light' | 'dark' | 'auto';
  onToken: (token: string) => void;
  onExpire: () => void;
  onError?: () => void;
}

export default function TurnstileWidget({
  siteKey,
  action,
  theme = 'auto',
  onToken,
  onExpire,
  onError,
}: TurnstileWidgetProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const callbacksRef = useRef({ onToken, onExpire, onError });
  callbacksRef.current = { onToken, onExpire, onError };

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    let cancelled = false;
    let widgetId: string | null = null;

    void loadApi()
      .then((api) => {
        if (cancelled || !hostRef.current) return;
        widgetId = api.render(hostRef.current, {
          sitekey: siteKey,
          theme,
          action,
          callback: (token) => callbacksRef.current.onToken(token),
          'expired-callback': () => callbacksRef.current.onExpire(),
          'error-callback': () => callbacksRef.current.onError?.(),
        });
      })
      .catch(() => {
        if (!cancelled) callbacksRef.current.onError?.();
      });

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          // Widget already gone — unmount during a StrictMode double-invoke.
        }
      }
    };
  }, [siteKey, action, theme]);

  return <div ref={hostRef} className="min-h-[65px]" />;
}
